import { useEffect, useCallback } from "react";
import { CirclePlus } from "lucide-react";
import NestedCard from "./Card";

const NestedCardsGroup = ({ 
    register, 
    errors, 
    setValue, 
    watch,
    groupName,
    fields,
    fieldTitle,
    mode
}) => {
    const watchItems = watch(groupName) || [];

    // 創建初始巢狀項目的函數
    const createInitialNestedItem = (field) => {
        if (field.type === 'nested-array') {
            // 為巢狀陣列創建一個初始項目
            const initialNestedItem = field.fields.reduce((acc, nestedField) => {
                if (nestedField.type === 'object') {
                    acc[nestedField.name] = nestedField.fields.reduce((obj, f) => {
                        obj[f.name] = '';
                        return obj;
                    }, {});
                } else if (nestedField.type === 'nested-array') {
                    acc[nestedField.name] = [createInitialNestedItem(nestedField)];
                } else {
                    acc[nestedField.name] = '';
                }
                return acc;
            }, {});
            return [initialNestedItem];
        } else if (field.type === 'object') {
            // 為物件類型創建初始值
            return field.fields.reduce((acc, f) => {
                acc[f.name] = '';
                return acc;
            }, {});
        }
        return '';
    };

    // 創建一個新的主項目
    const createNewItem = useCallback(() => {
        return fields.reduce((acc, field) => {
            if (field.type === 'nested-array') {
                acc[field.name] = [createInitialNestedItem(field)[0]];
            } else if (field.type === 'object') {
                acc[field.name] = createInitialNestedItem(field);
            } else {
                acc[field.name] = '';
            }
            return acc;
        }, { tempUuid: Date.now() });
    }, [fields]);

    // 初始化
    useEffect(() => {
        if (watchItems.length === 0) {
            setValue(groupName, [createNewItem()], {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: false
            });
        }
    }, []);


    const addNestedItem = useCallback((parentIndex, nestedFieldName) => {
        
        const newItems = [...watchItems];
        const fieldConfig = fields.find(f => f.name === nestedFieldName);

        if (!fieldConfig) {
            console.error('Field config not found for:', nestedFieldName);
            return;
        }

        if (!newItems[parentIndex][nestedFieldName]) {
            newItems[parentIndex][nestedFieldName] = [];
        }

        const newNestedItem = fieldConfig.fields.reduce((acc, field) => {
            acc[field.name] = '';
            return acc;
        }, {});

        newItems[parentIndex][nestedFieldName].push(newNestedItem);

        setValue(groupName, newItems, {
            shouldValidate: false,
            shouldDirty: true,
            shouldTouch: false
        });
    }, [groupName, setValue, watchItems, fields]);

    // 添加主項目
    const addItem = useCallback(() => {
        setValue(groupName, [...watchItems, createNewItem()], {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: false
        });
    }, [groupName, setValue, watchItems, createNewItem]);

    // 移除主項目
    const removeItem = useCallback((index) => {
        if (watchItems.length > 1) {
            const newItems = watchItems.filter((_, i) => i !== index);
            setValue(groupName, newItems, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: false
            });
        }
    }, [groupName, setValue, watchItems]);

    // 移除巢狀項目
    const removeNestedItem = useCallback((parentIndex, nestedFieldName, nestedIndex) => {
        const newItems = [...watchItems];
        const nestedArray = newItems[parentIndex][nestedFieldName];
        
        if (nestedArray && nestedArray.length > 1) {
            newItems[parentIndex][nestedFieldName] = nestedArray.filter((_, i) => i !== nestedIndex);
            
            setValue(groupName, newItems, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: false
            });
        }
    }, [groupName, setValue, watchItems]);


    return (
        <div className="space-y-6">
            {Array.isArray(watchItems) && watchItems.map((item, index) => (
                <NestedCard
                    key={item?.tempUuid || `${groupName}-${index}`}
                    index={index}
                    fields={fields}
                    register={register}
                    errors={errors}
                    watch={watch}
                    onRemove={() => removeItem(index)}
                    onAddNested={addNestedItem}
                    onRemoveNested={removeNestedItem}
                    groupName={groupName}
                    mode={mode}
                />
            ))}
            
            <button
                type="button"
                onClick={() => {
                    addItem();
                }}
                className="my-4 p-2 w-fit flex flex-col items-center justify-center text-gray-500 hover:text-gray-700 hover:border-gray-400"
            >
                <CirclePlus size={32} strokeWidth={1} />
                <span className="mt-1 text-sm">新增</span>
            </button>
        </div>
    );
};

export default NestedCardsGroup;